import { type RefObject, useLayoutEffect, useRef } from 'react';
import { type GsapBundle, loadGsap } from '../lib/gsap';

export type ChoreographySetup = (bundle: GsapBundle) => void;

/**
 * Runs one section's scroll choreography once GSAP has loaded.
 *
 * Every sequence is scoped to a `gsap.context` rooted at the section, so a
 * revert on unmount removes its tweens, its pins and its pin spacers. If the
 * component unmounts while GSAP is still in flight, nothing is set up at all.
 */
export function useScrollChoreography(
  scope: RefObject<HTMLElement | null>,
  setup: ChoreographySetup,
): void {
  // The setup closure is rebuilt every render; sections register exactly once,
  // so the latest closure is read through a ref rather than re-running the effect.
  const setupRef = useRef(setup);

  useLayoutEffect(() => {
    setupRef.current = setup;
  });

  useLayoutEffect(() => {
    let context: { revert: () => void } | undefined;
    let cancelled = false;
    let started = false;

    // Setting up a ScrollTrigger writes inline transforms onto the elements it
    // drives — including the hero headline, which is the Largest Contentful
    // Paint element. Doing that during the first paint re-paints the headline
    // and resets its LCP timestamp, so choreography waits for the browser to be
    // idle, for the reader to scroll, or for a 1200ms backstop, whichever lands
    // first.
    const begin = () => {
      if (started || cancelled) {
        return;
      }

      started = true;
      teardownTriggers();

      void loadGsap()
        .then((bundle) => {
          if (cancelled) {
            return;
          }

          context = bundle.gsap.context(() => setupRef.current(bundle), scope);
        })
        .catch((error: unknown) => {
          // Without this the six service rows keep the reveal's resting state
          // for the life of the page and the section renders blank.
          document.documentElement.classList.add('choreo-failed');
          console.error(
            '[black-bear] scroll choreography could not load; every section was revealed instead.',
            error,
          );
        });
    };

    const idle =
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback(begin, { timeout: 1200 })
        : window.setTimeout(begin, 300);
    const backstop = window.setTimeout(begin, 1200);

    function teardownTriggers() {
      if (typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idle as number);
      } else {
        window.clearTimeout(idle as number);
      }

      window.clearTimeout(backstop);
      window.removeEventListener('scroll', begin);
      window.removeEventListener('touchstart', begin);
    }

    window.addEventListener('scroll', begin, { passive: true, once: true });
    window.addEventListener('touchstart', begin, { passive: true, once: true });

    return () => {
      cancelled = true;
      teardownTriggers();
      context?.revert();
    };
  }, [scope]);
}
